/*
  Вкладка «Графики» на «Отчётах»: из чего сложился долг — §3.11.2.

  🔴 Место выбрано владельцем 29.09.2026 и не случайно: у графиков есть ПЕРИОД,
  который на этом экране уже выбран, а дашборд показывает срез портфеля на дату,
  и периоду там взяться неоткуда.

  🔴 **Период действует не на все графики, и под каждым это написано словами.**
  Начисления лежат с месяцем, за который начислены, поэтому «долг по месяцам»,
  «собираемость», «возраст долга» и «услуги» считаются за любой отрезок. А
  разбор счёта система хранит только текущий - каким он был в мае, не знает
  никто. Умолчать об этом значило бы показать срез на сегодня человеку, который
  выбрал квартал и читает экран как «за квартал».
*/

import { AMOUNT_TITLES, accounts as accountsWord, date, money, percent } from '../api/format.js'
import { HorizontalBars, VerticalBars } from './ChartBars.jsx'
import { PercentLine } from './ChartLine.jsx'
import ShareBar from './ChartShare.jsx'
import InfoHint from './InfoHint.jsx'

/* Подписывается каждый шестой месяц: сорок четыре подписи подряд сливаются в
   серую полосу, из которой не читается ни одна. */
const MONTH_LABEL_EVERY = 6

/* НАИМЕНЬШИЙ потолок шкалы собираемости. Сорок процентов, а не сто: на портфеле, где
   платят семнадцать процентов, линия по стопроцентной шкале лежит у самого
   низа и движения не показывает вовсе. Данные выше сорока потолок поднимают сами
   (`PercentLine`): обрезать значение по потолку нельзя - 70 % рисовалось как 40 %. */
const COLLECTION_MAX = 40

/*
  Сколько столбиков ещё можно подписать суммой. Больше - подписи наезжают друг
  на друга и превращают график в серую полосу: на демонстрационном портфеле дат
  сгорания три, но на портфеле в двадцать тысяч счетов их бывают десятки.
  Величина при этом не пропадает - она остаётся в подсказке под курсором.
*/
const LABELS_FIT = 6

/*
  🔴 Высота рисунка одна на все графики сетки (30.09.2026, замечание владельца:
  «прыгают размеры, что плохо в плане UI практик»). До этого каждый график нёс
  свою высоту из макета - 220, 190, 200, 180, - и карточки в сетке выходили
  разной высоты; глаз читает такой ряд как сбившийся, а не как набор равных
  ответов. Вместе с этим пояснение прижато к низу карточки (`.chart__why`,
  `margin-top: auto`), а шапке задана постоянная высота: иначе карточка с
  крупной цифрой была бы выше соседней без неё.
*/
const CHART_HEIGHT = 200

const everyBar = (rows) => (rows.length <= LABELS_FIT ? rows.map((row, index) => index) : [])

function monthShort(iso) {
  const [year, month] = iso.split('-')
  return `${month}.${year.slice(2)}`
}

/*
  Первая буква названия услуги - заглавная (30.09.2026, замечание владельца «не
  все слова с большой буквы»). В учётной системе заказчика услуги заведены
  вразнобой, и список выглядит набранным как попало.

  🔴 Меняется РЕГИСТР ПЕРВОЙ БУКВЫ и ничего больше: само название остаётся тем,
  что стоит в выгрузке. Приводить названия к общему справочнику нельзя - «ОДН
  ХВС» и «Горячее В/С (Энергия)» написаны так не случайно, и сложить «Содержание
  жилья» с «Содержанием жилого помещения» за заказчика мы не вправе.

  🔴 **Исключение, найденное осмотром в тот же час.** В боевой выгрузке есть
  «Содержание жилья» И «содержание жилья» - две РАЗНЫЕ строки учётной системы с
  разными суммами. Заглавная буква делала их неразличимыми на экране, а заодно
  роняла уникальность строк списка. Поэтому пара, которую отличает только эта
  буква, остаётся как есть: различие в регистре - единственное, что о ней
  сообщает выгрузка, и стирать его значит показать две одинаковые строки с
  разными числами.
*/
function capitalised(title) {
  return title ? title.charAt(0).toUpperCase() + title.slice(1) : title
}

function serviceNames(rows) {
  const seen = new Map()
  for (const row of rows) {
    const key = row.title.toLowerCase()
    seen.set(key, (seen.get(key) ?? 0) + 1)
  }
  return rows.map((row) => ({
    row,
    shown: seen.get(row.title.toLowerCase()) > 1 ? row.title : capitalised(row.title),
  }))
}

function ChartCard({ title, hero, heroTone, hint, note, children }) {
  return (
    <section className="card">
      <div className="chart__head">
        <h3 className="chart__title">{title}</h3>
        {hero ? (
          <div className={`chart__hero${heroTone ? ` chart__hero--${heroTone}` : ''}`}>{hero}</div>
        ) : null}
        {hint ? <InfoHint title="Откуда цифра" text={hint} /> : null}
      </div>
      {children}
      {note ? <p className="chart__why">{note}</p> : null}
    </section>
  )
}

export default function PortfolioCharts({ detail }) {
  const months = detail.months.map((month) => ({
    title: month.title,
    short: monthShort(month.title),
    value: month.debt,
    caption: money(month.debt),
    note: `начислено ${money(month.charged)}, оплачено ${money(month.paid)}`,
  }))

  const collection = detail.months.map((month) => ({
    title: monthShort(month.title),
    short: monthShort(month.title),
    value: Number(month.charged) ? (Number(month.paid) / Number(month.charged)) * 100 : 0,
    note: `оплачено ${percent(Number(month.charged) ? Number(month.paid) / Number(month.charged) : null)} от начисленного (${money(month.paid)} из ${money(month.charged)})`,
  }))

  const years = detail.years.map((year) => ({
    title: year.title,
    value: year.debt,
    caption: money(year.debt),
    hero: money(year.debt, { short: true }),
    note: `начислено ${money(year.charged)}, не оплачено ${percent(Number(year.charged) ? Number(year.debt) / Number(year.charged) : null)}`,
  }))

  const services = serviceNames(detail.services).map(({ row, shown }) => ({
    // Ключ - название ИЗ ВЫГРУЗКИ, а не показанное: показанные у двух услуг
    // могут совпасть, и строки списка потеряли бы уникальность.
    key: row.title,
    title: shown,
    value: row.debt,
    caption: money(row.debt),
    note: `начислено ${money(row.charged)}`,
  }))

  const burning = detail.burning.map((item) => ({
    title: date(item.when),
    value: item.amount,
    caption: money(item.amount),
    hero: money(item.amount, { short: true }),
    note: accountsWord(item.accounts),
  }))

  const paidShare = Number(detail.totals.charged)
    ? Number(detail.totals.paid) / Number(detail.totals.charged)
    : null

  return (
    <div className="grid grid--two">
      <ChartCard
        title="Долг по месяцу начисления"
        hero={money(detail.totals.debt)}
        hint="Столбик - неоплаченный остаток начислений этого месяца. Долг растёт не только оттого, что платят хуже: в портфель попадают новые счета вместе со своей историей."
        note="За выбранный период. Месяц - тот, ЗА который начислено, а не тот, в котором загрузили выгрузку."
      >
        <VerticalBars
          rows={months}
          height={CHART_HEIGHT}
          every={MONTH_LABEL_EVERY}
          mark={months.length ? [months.length - 1] : []}
          alt="Неоплаченный долг по месяцу начисления"
        />
      </ChartCard>

      <ChartCard
        title="Сколько из начисленного оплачено"
        hero={percent(paidShare)}
        hint="«Оплачено» - то, что отмечено оплаченным в вашей учётной системе на день последнего обмена. Это не деньги, дошедшие до расчётного счёта: выписка в Арбитр ЖКХ не загружается."
        note="За выбранный период. Своя шкала, потому что здесь проценты, а рядом рубли: одна ось на два разных измерения врёт."
      >
        <PercentLine
          rows={collection}
          height={CHART_HEIGHT}
          every={MONTH_LABEL_EVERY}
          max={COLLECTION_MAX}
          average={paidShare === null ? null : Number((paidShare * 100).toFixed(1))}
          alt="Доля оплаченного от начисленного по месяцам"
        />
      </ChartCard>

      <ChartCard
        title="Когда сгорит ближайшее начисление"
        hero={money(detail.burning.reduce((sum, item) => sum + Number(item.amount), 0))}
        heroTone="burn"
        hint="Арбитр ЖКХ хранит по счёту ОДНУ ближайшую дату, когда выпадает очередное начисление. Что сгорит после неё, пока не считается."
        note="Срез на сегодня, период на него не действует. Это не календарь всего сгорания: полный календарь считается по каждому начислению - отдельная работа."
      >
        <VerticalBars
          rows={burning}
          every={1}
          mark={everyBar(burning)}
          height={CHART_HEIGHT}
          tone="burn"
          alt="Когда сгорит ближайшее начисление"
        />
      </ChartCard>

      <ChartCard
        title="Возраст долга: в каком году возник"
        hint="Год берётся из месяца начисления. Свежий долг возвращается кратно лучше старого - это главный довод в пользу быстрой реакции."
        note="За выбранный период."
      >
        <VerticalBars
          rows={years}
          every={1}
          mark={everyBar(years)}
          height={CHART_HEIGHT}
          alt="Долг по годам возникновения"
        />
      </ChartCard>

      <ChartCard
        title="Где лежит долг"
        hint="Крупный счёт - тот же, что крупный счёт в реестре: от ста тысяч. Когда десятая часть счетов держит две трети долга, очередь к подаче строится по ним."
        note="Срез на сегодня, период на него не действует."
      >
        <ShareBar
          segments={[
            {
              title: `${accountsWord(detail.concentration.large_accounts)} свыше 100 тыс`,
              value: detail.concentration.large_amount,
              tone: 'strong',
            },
            {
              title: `остальные ${accountsWord(detail.concentration.rest_accounts)}`,
              value: detail.concentration.rest_amount,
              tone: 'light',
            },
          ]}
          alt="Доля крупных счетов в долге портфеля"
        />
      </ChartCard>

      <ChartCard
        title="Портфель по размеру долга"
        hint="Те же четыре полосы, по которым отбирает реестр. Наведите курсор - под каждой полосой число счетов."
        note="Срез на сегодня, период на него не действует."
      >
        <VerticalBars
          rows={detail.amount_bands.map((band) => ({
            /* Название полосы берётся из справочника кабинета, а не с сервера:
               второй экземпляр тех же названий разошёлся бы с первым при
               первом же переименовании (сторожит test_titles_match_enums). */
            title: AMOUNT_TITLES[band.band],
            value: band.amount,
            caption: money(band.amount),
            note: accountsWord(band.accounts),
          }))}
          every={1}
          mark={everyBar(detail.amount_bands)}
          height={CHART_HEIGHT}
          alt="Портфель по размеру долга"
        />
      </ChartCard>

      <div className="chart__wide">
        <ChartCard
          title="Из каких услуг сложился долг"
          hint="Названия - как в вашей учётной системе. Одна услуга под старым и новым названием показана двумя строками: сложить их за вас нельзя."
          note="За выбранный период."
        >
          <HorizontalBars rows={services} alt="Долг по услугам" />
        </ChartCard>
      </div>
    </div>
  )
}
